<template>
  <div
    :id="`word-cloud-${id}`"
    class="word-cloud-view"
    :class="{
      'is-allow-text-removal': isAllowedTextRemoval,
    }"
  ></div>
</template>

<script>
import mixpanelUtils from '@/utils/mixpanel.utils'

const { d3 } = window
const { cloud } = window.d3.layout

export default {
  name: 'WordCloudView',
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    words: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      default: 1180,
    },
    height: {
      type: Number,
      default: 480,
    },
    font: {
      type: String,
      default: 'sans-serif',
    },
    fill: {
      type: Function,
      default: (word) => word.color,
    },
    rotate: {
      type: Function,
      default: () => 0,
    },
    onWordClick: {
      type: Function,
      default: () => {},
    },
    isAllowedTextRemoval: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    wordCloudId() {
      return `#word-cloud-${this.id}`
    },
    wordsSelector() {
      return `${this.wordCloudId} svg g`
    },
  },

  watch: {
    words(newWords, oldWords) {
      if (!this.isWordsChange(newWords, oldWords)) return
      this.renderWords(newWords)
    },
  },
  mounted() {
    this.makeWordContainer(this.wordCloudId)
    this.renderWords(this.words)
    if (this.$options?.name) {
      mixpanelUtils.track(this.$options.name)
    }
  },
  methods: {
    makeWordContainer() {
      return d3
        .select(this.wordCloudId)
        .append('svg')
        .attr('width', this.width)
        .attr('height', this.height)
        .append('g')
        .attr('transform', `translate(${this.width / 2}, ${this.height / 2})`)
    },
    getWordContainer() {
      return d3.select(this.wordsSelector)
    },
    draw(words) {
      const container = this.getWordContainer()
        .selectAll('g text')
        .data(words, (d) => {
          return d.originalText
        })
      container
        .enter()
        .append('text')
        .style('font-family', this.font)
        .style('font-weight', 'bold')
        .style('fill', this.fill)
        .attr('text-anchor', 'middle')
        .text((d) => {
          return d.text
        })
        .on('click', this.onWordClick)

      container
        .transition()
        .duration(600)
        .style('font-size', (d) => {
          return `${d.size}px`
        })
        .attr('transform', (d) => {
          return `translate(${d.x}, ${d.y}) rotate(${d.rotate})`
        })
        .style('fill-opacity', 1)

      container
        .exit()
        .transition()
        .duration(200)
        .style('fill-opacity', 1e-6)
        .remove()
    },

    isWordsChange(newWords, oldWords) {
      if (newWords.length !== oldWords.length) return true
      const mapper = (word) => ({
        text: word.text,
        count: word.count,
      })
      const mappedNewWords = newWords.map(mapper)
      const mappedOldWords = oldWords.map(mapper)
      return JSON.stringify(mappedNewWords) !== JSON.stringify(mappedOldWords)
    },

    renderWords(words) {
      cloud()
        .size([this.width, this.height])
        .words(words)
        .padding(4)
        .rotate(this.rotate)
        .font(this.font)
        .fontSize((d) => {
          return d.size
        })
        .on('end', this.draw)
        .start()
    },

  },
}

</script>

<style lang="scss" scoped>
.word-cloud-view {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.is-allow-text-removal ::v-deep text:hover {
  cursor: pointer;
  text-decoration: line-through;
}
</style>
